import type { InputMessage } from '../shared/datachannel';

// 触屏 → 鼠标输入映射。
// - 只把视频真实画面区域映射为鼠标坐标，object-fit: contain 产生的黑边不响应。
// - 默认：单指拖动 = 移动光标（悬停）；轻点 = 左键单击。
// - 长按(450ms)不移动 = 右键单击。
// - 拖拽模式(开)：单指 = 按住左键拖动（文字选择/框选）。
// - 双指拖动 = 滚轮滚动（垂直/水平）。

export interface TouchState {
  rightMode(): boolean;
  dragMode(): boolean;
}

type InputSender = (msg: InputMessage) => void;
type Point = { x: number; y: number };

export function attachTouch(el: HTMLElement, send: InputSender, state: TouchState): void {
  let sx = 0, sy = 0, startT = 0, moved = false;
  let active = false;
  let multiTouch = false;
  let endInside = false;
  let btnDown = false; // 拖拽模式按住的左键
  let longFired = false; // 长按右键已触发
  let longTimer: ReturnType<typeof setTimeout> | null = null;
  let twoPrev: Point | null = null;
  let lastPoint: Point | null = null;

  const clamp = (value: number) => Math.min(1, Math.max(0, value));

  /**
   * video 元素通常铺满整个 stage，但 object-fit: contain 会在四周留下黑边。
   * 这里根据实际视频帧比例算出真正显示的画面矩形，避免把黑边映射到远端屏幕边缘。
   */
  const contentRect = () => {
    const box = el.getBoundingClientRect();
    const video = el as HTMLVideoElement;
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    if (box.width <= 0 || box.height <= 0 || videoWidth <= 0 || videoHeight <= 0) {
      return null;
    }

    const scale = Math.min(box.width / videoWidth, box.height / videoHeight);
    const width = videoWidth * scale;
    const height = videoHeight * scale;
    return {
      left: box.left + (box.width - width) / 2,
      top: box.top + (box.height - height) / 2,
      width,
      height,
    };
  };

  const pointFromClient = (clientX: number, clientY: number): Point | null => {
    const rect = contentRect();
    if (!rect) return null;

    const right = rect.left + rect.width;
    const bottom = rect.top + rect.height;
    if (clientX < rect.left || clientX > right || clientY < rect.top || clientY > bottom) {
      return null;
    }

    return {
      x: clamp((clientX - rect.left) / rect.width),
      y: clamp((clientY - rect.top) / rect.height),
    };
  };

  const pointFromTouch = (touch: Touch) => pointFromClient(touch.clientX, touch.clientY);

  const avg = (list: TouchList): Point => {
    const a = list[0], b = list[1];
    return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
  };

  const clearLong = () => {
    if (longTimer) {
      clearTimeout(longTimer);
      longTimer = null;
    }
  };

  const resetGesture = () => {
    active = false;
    multiTouch = false;
    endInside = false;
    twoPrev = null;
    lastPoint = null;
  };

  const sendButton = (
    type: 'mousedown' | 'mouseup',
    button: 'left' | 'right',
    point: Point | null,
  ) => {
    send({ type, button, ...(point ? { x: point.x, y: point.y } : {}) });
  };

  const sendClick = (button: 'left' | 'right', point: Point) => {
    // 手机轻触没有“悬停后再按下”的可靠保证。把坐标和完整点击放在同一条
    // 可靠控制消息中，避免可丢弃 mousemove 晚到时在旧位置执行点击。
    send({ type: 'click', button, x: point.x, y: point.y });
  };

  el.addEventListener('touchstart', (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.touches.length === 1) {
      clearLong();
      const t = e.touches[0];
      const p = pointFromTouch(t);

      sx = t.clientX;
      sy = t.clientY;
      startT = Date.now();
      moved = p === null;
      longFired = false;
      multiTouch = false;
      endInside = p !== null;
      active = p !== null;
      twoPrev = null;
      lastPoint = p;

      // 还没有视频帧，或触摸落在 contain 黑边：整个手势不产生远端输入。
      if (!p) return;

      send({ type: 'mousemove', x: p.x, y: p.y });
      if (state.dragMode()) {
        btnDown = true;
        sendButton('mousedown', 'left', p);
      } else {
        longTimer = setTimeout(() => {
          longTimer = null;
          if (active && endInside && !moved && !multiTouch) {
            longFired = true;
            sendClick('right', p);
          }
        }, 450);
      }
    } else if (e.touches.length >= 2) {
      clearLong();
      if (!active) {
        multiTouch = true;
        twoPrev = null;
        return;
      }

      multiTouch = true;
      moved = true;
      if (btnDown) {
        sendButton('mouseup', 'left', lastPoint);
        btnDown = false;
      }

      const center = avg(e.touches);
      twoPrev = pointFromClient(center.x, center.y) ? center : null;
    }
  }, { passive: false });

  el.addEventListener('touchmove', (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!active) return;

    if (e.touches.length === 1) {
      const t = e.touches[0];
      if (multiTouch) {
        // 双指滚动抬起一根手指后，不把剩余手指当成单指移动/点击。
        moved = true;
        twoPrev = null;
        return;
      }

      if (Math.abs(t.clientX - sx) > 8 || Math.abs(t.clientY - sy) > 8) moved = true;
      clearLong();

      const p = pointFromTouch(t);
      if (!p) {
        endInside = false;
        moved = true;
        return;
      }

      endInside = true;
      lastPoint = p;
      send({ type: 'mousemove', x: p.x, y: p.y });
    } else if (e.touches.length >= 2) {
      multiTouch = true;
      moved = true;
      clearLong();

      const center = avg(e.touches);
      const centerPoint = pointFromClient(center.x, center.y);
      if (!centerPoint) {
        // 双指中心在黑边时不滚动；重新进入画面后从新基准开始，避免跳变。
        twoPrev = null;
        return;
      }
      if (!twoPrev) {
        twoPrev = center;
        return;
      }

      const dx = center.x - twoPrev.x;
      const dy = center.y - twoPrev.y;
      send({ type: 'wheel', deltaX: -dx * 2, deltaY: -dy * 2 });
      twoPrev = center;
    }
  }, { passive: false });

  el.addEventListener('touchend', (e) => {
    e.preventDefault();
    e.stopPropagation();
    clearLong();

    if (!active) {
      if (e.touches.length === 0) resetGesture();
      return;
    }

    if (e.touches.length >= 1) {
      // 抬起一根手指（如双指滚动剩一根）：更新状态，不触发点击。
      moved = true;
      twoPrev = null;
      return;
    }

    const changedTouch = e.changedTouches[0];
    const endedPoint = changedTouch ? pointFromTouch(changedTouch) : lastPoint;
    if (changedTouch) endInside = endedPoint !== null;
    if (endedPoint) lastPoint = endedPoint;

    if (btnDown) {
      // 最终坐标与 mouseup 一起走可靠通道，保证拖拽落点不会依赖最后一个
      // 可能被丢弃的 mousemove。
      sendButton('mouseup', 'left', lastPoint);
      btnDown = false;
    } else if (!multiTouch && endInside && lastPoint && !longFired && !moved && Date.now() - startT < 450) {
      const button = state.rightMode() ? 'right' : 'left';
      sendClick(button, lastPoint);
    }

    resetGesture();
  }, { passive: false });

  el.addEventListener('touchcancel', (e) => {
    e.preventDefault();
    e.stopPropagation();
    clearLong();
    if (btnDown) {
      sendButton('mouseup', 'left', lastPoint);
      btnDown = false;
    }
    resetGesture();
  }, { passive: false });
}
